import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import SearchableSelect from "../common/SearchableSelect";

const WEEKDAY_IDS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const actionTitles = {
  "lesson-details": "Zmień dane zajęć",
  "absence-report": "Zgłoszenie nieobecności",
  "lesson-cancellation": "Odwołaj lekcję",
  "undo-cancellation": "Cofnij odwołanie lekcji",
  "student-list": "Zarządzaj listą uczniów",
};

const getDefaultReportedAtValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
};

const formatPersonName = person => (
  [person.firstName, person.lastName].filter(Boolean).join(" ") || person.id
);

const formatLessonDateDayOfWeek = (date) => {
  const dateInputMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const dayOfWeek = dateInputMatch
    ? new Date(Date.UTC(
      Number(dateInputMatch[1]),
      Number(dateInputMatch[2]) - 1,
      Number(dateInputMatch[3]),
    )).getUTCDay()
    : new Date(date).getDay();

  return Number.isNaN(dayOfWeek)
    ? null
    : WEEKDAY_IDS[dayOfWeek];
};

function AttendanceCommandModals({
  activeAction,
  viewModel,
  teachers,
  canEditLessonComment,
  canEditLessonDetails,
  canManageStudents,
  allStudents,
  classStudents,
  canUseAdministrativeCommands,
  administrativeCommandBlockReason,
  canUndoWholeLessonCancellation,
  onClose,
  onUpdateLessonDetails,
  onAddStudent,
  onRemoveStudent,
  onReportAbsence,
  onCancelWholeLesson,
  onUndoWholeLessonCancellation,
}) {
  const [commandError, setCommandError] = useState("");
  const [lessonDetailsDraft, setLessonDetailsDraft] = useState(viewModel.lessonDetails);
  const [absenceDraft, setAbsenceDraft] = useState({
    studentId: "",
    reportedAt: getDefaultReportedAtValue(),
    note: "",
  });
  const [studentToAdd, setStudentToAdd] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");

  useEffect(() => {
    if (!activeAction) return;
    setCommandError("");
    setLessonDetailsDraft(viewModel.lessonDetails);
    setAbsenceDraft({
      studentId: "",
      reportedAt: getDefaultReportedAtValue(),
      note: "",
    });
    setStudentToAdd("");
    setCancellationReason("");
  }, [activeAction, viewModel.lessonDetails, viewModel.scheduledLesson.id]);

  if (!activeAction) return null;

  const resetAndClose = () => {
    setCommandError("");
    onClose();
  };

  const handleCommand = (command, fallbackMessage) => {
    setCommandError("");
    command()
      .then(resetAndClose)
      .catch(error => setCommandError(error.message || fallbackMessage));
  };

  const handleStudentCommand = (command, fallbackMessage) => {
    setCommandError("");
    command()
      .catch(error => setCommandError(error.message || fallbackMessage));
  };

  const activeTeachers = teachers.filter(teacher => !teacher.archived && !teacher.deleted);

  const renderLessonDetailsForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleCommand(
          () => onUpdateLessonDetails(lessonDetailsDraft),
          "Nie udało się zapisać danych zajęć.",
        );
      }}
    >
      <div className="attendance-page__field">
        <span>Data</span>
        <input
          aria-label="Data"
          className="form-control mb-3"
          type="date"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.date || ""}
          onChange={event => setLessonDetailsDraft({
            ...lessonDetailsDraft,
            date: event.target.value,
            dayOfWeek: formatLessonDateDayOfWeek(event.target.value),
          })}
        />
      </div>
      <div className="attendance-page__field">
        <span>Godzina</span>
        <input
          aria-label="Godzina"
          className="form-control mb-3"
          type="time"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.startTime || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, startTime: event.target.value })}
        />
      </div>
      <div className="attendance-page__field">
        <span>Lektor</span>
        <select
          aria-label="Lektor"
          className="form-control mb-3"
          disabled={!canEditLessonDetails}
          value={lessonDetailsDraft.teacherId || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, teacherId: event.target.value })}
        >
          {activeTeachers.map(teacher => (
            <option key={teacher.id} value={teacher.id}>{`${teacher.firstName} ${teacher.lastName}`}</option>
          ))}
        </select>
      </div>
      <div className="attendance-page__field">
        <span>Uwagi</span>
        <textarea
          aria-label="Uwagi"
          className="form-control mb-3"
          disabled={!canEditLessonComment}
          value={lessonDetailsDraft.comment || ""}
          onChange={event => setLessonDetailsDraft({ ...lessonDetailsDraft, comment: event.target.value })}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn btn-light" onClick={resetAndClose}>Anuluj</button>
        <button type="submit" className="btn btn-success">Zapisz zmiany</button>
      </div>
    </form>
  );

  const renderAbsenceForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!absenceDraft.studentId) {
          setCommandError("Wybierz ucznia.");
          return;
        }
        if (!absenceDraft.reportedAt || Number.isNaN(new Date(absenceDraft.reportedAt).getTime())) {
          setCommandError("Podaj prawidłowy czas zgłoszenia.");
          return;
        }
        handleCommand(
          () => onReportAbsence({
            studentId: absenceDraft.studentId,
            reportedAt: new Date(absenceDraft.reportedAt).toISOString(),
            note: absenceDraft.note,
          }),
          "Nie udało się zapisać zgłoszenia nieobecności.",
        );
      }}
    >
      <div className="attendance-page__field">
        <span>Uczeń</span>
        <select
          aria-label="Uczeń"
          className="form-control mb-3"
          value={absenceDraft.studentId}
          onChange={event => setAbsenceDraft({ ...absenceDraft, studentId: event.target.value })}
        >
          <option value="">Wybierz ucznia</option>
          {viewModel.participants.map(participant => (
            <option key={participant.id} value={participant.id}>{formatPersonName(participant)}</option>
          ))}
        </select>
      </div>
      <div className="attendance-page__field">
        <span>Kiedy otrzymaliśmy zgłoszenie</span>
        <input
          aria-label="Kiedy otrzymaliśmy zgłoszenie"
          className="form-control mb-3"
          type="datetime-local"
          value={absenceDraft.reportedAt}
          onChange={event => setAbsenceDraft({ ...absenceDraft, reportedAt: event.target.value })}
        />
      </div>
      <div className="attendance-page__field">
        <span>Notatka</span>
        <textarea
          aria-label="Notatka"
          className="form-control mb-3"
          value={absenceDraft.note}
          onChange={event => setAbsenceDraft({ ...absenceDraft, note: event.target.value })}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn btn-light" onClick={resetAndClose}>Anuluj</button>
        <button type="submit" className="btn btn-warning">Zapisz zgłoszenie</button>
      </div>
    </form>
  );

  const renderCancellationForm = () => (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!cancellationReason.trim()) {
          setCommandError("Podaj powód odwołania lekcji.");
          return;
        }
        handleCommand(
          () => onCancelWholeLesson({ reason: cancellationReason }),
          "Nie udało się odwołać lekcji.",
        );
      }}
    >
      <div className="attendance-page__field">
        <span>Powód odwołania lekcji</span>
        <textarea
          aria-label="Powód odwołania lekcji"
          className="form-control mb-3"
          value={cancellationReason}
          onChange={event => setCancellationReason(event.target.value)}
        />
      </div>
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn btn-light" onClick={resetAndClose}>Anuluj</button>
        <button type="submit" className="btn btn-danger">Potwierdź odwołanie</button>
      </div>
    </form>
  );

  const activeClassStudentIds = classStudents
    .filter(classStudent => classStudent.schoolClassId === viewModel.schoolClass.id)
    .map(classStudent => classStudent.studentId);
  const participantIds = viewModel.participants.map(student => student.id);
  const studentsAvailableToAdd = allStudents
    .filter(student => !student.deleted && !student.archived)
    .filter(student => !activeClassStudentIds.includes(student.id) && !participantIds.includes(student.id));

  const handleAddStudent = (event) => {
    event.preventDefault();
    if (!studentToAdd) {
      setCommandError("Wybierz ucznia do dodania.");
      return;
    }
    handleStudentCommand(
      () => onAddStudent(studentToAdd).then(() => setStudentToAdd("")),
      "Nie udało się dodać ucznia do grupy.",
    );
  };

  const handleRemoveStudent = studentId => handleStudentCommand(
    () => onRemoveStudent(studentId),
    "Nie udało się usunąć ucznia z grupy.",
  );

  const renderStudentList = () => (
    <div>
      {!canManageStudents && <div className="alert alert-warning">Brak uprawnień do edycji listy uczniów.</div>}
      <div className="attendance-page__student-list" role="list" aria-label="Aktualni uczniowie">
        {viewModel.participants.map(student => (
          <div key={student.id} className="attendance-page__student-list-row" role="listitem">
            <span>{formatPersonName(student)}</span>
            {canManageStudents && activeClassStudentIds.includes(student.id) && (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveStudent(student.id)}>
                Usuń
              </button>
            )}
          </div>
        ))}
      </div>
      {canManageStudents && (
        <form onSubmit={handleAddStudent}>
          <SearchableSelect
            name="studentToAdd"
            label="Dodaj ucznia"
            placeholder="Wpisz imię lub nazwisko ucznia…"
            value={studentToAdd}
            onChange={event => setStudentToAdd(event.target.value)}
            options={studentsAvailableToAdd.map(student => ({ value: student.id, text: formatPersonName(student) }))}
          />
          <button type="submit" className="btn btn-outline-success">Dodaj ucznia</button>
        </form>
      )}
      <div className="attendance-page__modal-footer">
        <button type="button" className="btn btn-light" onClick={resetAndClose}>Zamknij</button>
      </div>
    </div>
  );

  const renderContent = () => {
    if (activeAction === "lesson-details" && !canEditLessonComment && !canEditLessonDetails) {
      return <div className="alert alert-warning">Brak uprawnień do tej akcji.</div>;
    }

    if (
      (activeAction === "absence-report" || activeAction === "lesson-cancellation") &&
      !canUseAdministrativeCommands
    ) {
      const messages = {
        role: "Brak uprawnień do tej akcji.",
        missingLesson: "Nie znaleziono lekcji, której dotyczy ta akcja.",
        past: "Ta lekcja już się rozpoczęła albo minęła. Nie można zgłosić nieobecności z tego ekranu.",
        checked: "Obecność dla tej lekcji została już zapisana. Nie można zgłosić nieobecności po sprawdzeniu obecności.",
        cancelled: "Lekcja jest odwołana. Nie można zgłosić nieobecności ucznia dla odwołanej lekcji.",
      };

      return (
        <div className="alert alert-warning">
          {messages[administrativeCommandBlockReason] || "Ta akcja nie jest teraz dostępna."}
        </div>
      );
    }

    if (activeAction === "lesson-details") return renderLessonDetailsForm();
    if (activeAction === "absence-report") return renderAbsenceForm();
    if (activeAction === "lesson-cancellation") return renderCancellationForm();
    if (activeAction === "student-list") return renderStudentList();

    if (activeAction === "undo-cancellation" && canUndoWholeLessonCancellation) {
      return (
        <div>
          <p>Czy na pewno cofnąć odwołanie tej lekcji?</p>
          <div className="attendance-page__modal-footer">
            <button type="button" className="btn btn-light" onClick={resetAndClose}>Anuluj</button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleCommand(onUndoWholeLessonCancellation, "Nie udało się cofnąć odwołania lekcji.")}
            >
              Cofnij odwołanie
            </button>
          </div>
        </div>
      );
    }

    return <div className="alert alert-warning">Ta akcja nie jest teraz dostępna.</div>;
  };

  return (
    <div className="attendance-page__modal-placeholder" role="dialog" aria-modal="true">
      <div className="attendance-page__modal-card">
        <button type="button" className="close" aria-label="Zamknij" onClick={resetAndClose}>x</button>
        <div className="modal-title">{actionTitles[activeAction] || "Akcja lekcji"}</div>
        {commandError && <div className="alert alert-danger" role="alert">{commandError}</div>}
        {renderContent()}
      </div>
    </div>
  );
}

AttendanceCommandModals.propTypes = {
  activeAction: PropTypes.string,
  viewModel: PropTypes.object.isRequired,
  teachers: PropTypes.array.isRequired,
  canEditLessonComment: PropTypes.bool.isRequired,
  canEditLessonDetails: PropTypes.bool.isRequired,
  canManageStudents: PropTypes.bool.isRequired,
  allStudents: PropTypes.array.isRequired,
  classStudents: PropTypes.array.isRequired,
  canUseAdministrativeCommands: PropTypes.bool.isRequired,
  administrativeCommandBlockReason: PropTypes.string,
  canUndoWholeLessonCancellation: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdateLessonDetails: PropTypes.func.isRequired,
  onAddStudent: PropTypes.func.isRequired,
  onRemoveStudent: PropTypes.func.isRequired,
  onReportAbsence: PropTypes.func.isRequired,
  onCancelWholeLesson: PropTypes.func.isRequired,
  onUndoWholeLessonCancellation: PropTypes.func.isRequired,
};

AttendanceCommandModals.defaultProps = {
  activeAction: "",
  administrativeCommandBlockReason: "",
};

export default AttendanceCommandModals;
